import { DEFAULT_CHESS_CLOCK_CONTROL } from '@/views/main/clock/utils/chess-clock';

export interface HandshakeGamePlayer {
  id: string;
  username: string;
  role: 'initiator' | 'responder';
  isPasserby?: boolean;
  /** Written as it is, without the @ a member's name carries: someone who is not signed in. */
  isGuest?: boolean;
  /** Their photo, as whoever opens the sheet already knows it, so the face does not wait for the handshake to load. */
  image?: string | null;
}

/** A player's name as the sheet and the clock both write it. */
export function formatGamePlayerLabel(player: HandshakeGamePlayer): string {
  return player.isPasserby || player.isGuest ? player.username : `@${player.username}`;
}

/**
 * A passerby whose code is still live is named with that code, so the same
 * line that says who they are also says what to write down.
 */
export function passerbyDisplayName(name: string, code: string | null | undefined, live: boolean | undefined): string {
  if (!live || !code) return name;

  return `${name} ${code}`;
}

export interface HandshakeGameSuggestion {
  white: HandshakeGamePlayer;
  black: HandshakeGamePlayer;
  minutes: number;
  increment: number;
  backRank: string[];
}

/** A back rank as one of the two players has it in front of them. */
export interface HandshakeGameRank {
  /** FEN letters, cased for the side this player has. */
  pieces: string[];
}

/**
 * How long a handshake stays live. Inside it, shaking again at the same spot or
 * on a profile is that handshake, reopened if it was closed. Another spot is a
 * new one, which closes the one before. Past the window the handshake is
 * history and the next tap makes a new one.
 *
 * Mirrors HANDSHAKE_WINDOW_HOURS on the server, which is what actually decides
 * whether a new handshake is created.
 */
export const HANDSHAKE_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * Whether a handshake is still the live one, as of the given moment. There is
 * no lower bound: a handshake dated a little ahead of the moment given is one
 * just made, read either off a clock that ticks on an interval or off a device
 * running behind the server, and it is as live as a handshake gets.
 */
export function isHandshakeLive(createdAt: string, now: Date): boolean {
  return now.getTime() - new Date(createdAt).getTime() < HANDSHAKE_WINDOW_MS;
}

interface HandshakeGameAvailability {
  initiatorId: string | null;
  responderId: string | null;
  isConfirmed: boolean;
  isPasserbyPending?: boolean;
  createdAt: string;
  closedAt?: string | null;
}

/**
 * A game recommendation belongs to the meeting the handshake was made at, so it
 * lasts as long as that handshake stays the live one, or until either player
 * closes it. The member who owns an unclaimed passerby row can still use it
 * with the person standing in front of them.
 */
export function canOpenHandshakeGame(
  handshake: HandshakeGameAvailability,
  userId: string | null | undefined,
  now: Date,
): boolean {
  if (!userId || !handshake.isConfirmed || handshake.closedAt) return false;
  if (!isHandshakeLive(handshake.createdAt, now)) return false;

  const isParticipant = handshake.initiatorId === userId || handshake.responderId === userId;
  if (!isParticipant) return false;

  const hasTwoPlayers = !!handshake.initiatorId && !!handshake.responderId;
  const isOwnPendingPasserby = handshake.isPasserbyPending && handshake.initiatorId === userId;

  return !!hasTwoPlayers || !!isOwnPendingPasserby;
}

/** What a tap on a player does: open the live game, reopen a closed one, or shake hands. */
export type HandshakeCta = 'open' | 'reopen' | 'handshake';

/** Where the player is looking at the other person. Empty on a profile. A gambit is part of its spot. */
export interface HandshakeHere {
  locationId?: string | null;
}

interface LiveCandidate {
  locationId: string | null;
  closedAt: string | null;
  createdAt: string;
  liveUntil: string;
}

/**
 * The handshake a tap here leads to, the same pick the server makes: one at
 * this spot first, then an open one over a closed one, then the newest. One
 * with no spot, or a look from a profile, counts as the same place. A handshake
 * at another spot is a meeting that ended there.
 */
export function pickLiveHandshake<T extends LiveCandidate>(candidates: T[], here: HandshakeHere, now: Date): T | null {
  const samePlace = candidates.filter(
    (handshake) =>
      new Date(handshake.liveUntil).getTime() > now.getTime() &&
      (!handshake.locationId || !here.locationId || handshake.locationId === here.locationId),
  );

  const rank = (handshake: T): number[] => [
    here.locationId && handshake.locationId === here.locationId ? 0 : 1,
    handshake.closedAt ? 1 : 0,
    -new Date(handshake.createdAt).getTime(),
  ];

  return (
    [...samePlace].sort((a, b) => {
      const [rankA, rankB] = [rank(a), rank(b)];
      return rankA.map((value, index) => value - rankB[index]).find((difference) => difference !== 0) ?? 0;
    })[0] ?? null
  );
}

export function handshakeCta(picked: { closedAt: string | null } | null): HandshakeCta {
  if (!picked) return 'handshake';

  return picked.closedAt ? 'reopen' : 'open';
}

/**
 * A confirmed handshake at this gambit whose recommendation has closed. The
 * disc stays, with the filled hand, and a tap only says the two already met here.
 */
export function hasSpentGambitHandshake(
  handshakes: {
    gambitId?: string | null;
    initiatorId: string | null;
    responderId: string | null;
    isConfirmed: boolean;
    createdAt: string;
  }[],
  gambitId: string | null | undefined,
  meId: string | null | undefined,
  playerId: string,
  now: Date,
): boolean {
  if (!gambitId || !meId) return false;

  return handshakes.some(
    (handshake) =>
      handshake.isConfirmed &&
      handshake.gambitId === gambitId &&
      !isHandshakeLive(handshake.createdAt, now) &&
      ((handshake.initiatorId === meId && handshake.responderId === playerId) ||
        (handshake.initiatorId === playerId && handshake.responderId === meId)),
  );
}

/** At least one position was played (answered yes) on this handshake. */
export function hasPlayedPosition(handshake: { positions?: number[] | null } | null | undefined): boolean {
  return !!handshake?.positions?.length;
}

/** The API's error key for an action on a handshake one of the two closed. */
export const HANDSHAKE_CLOSED_ERROR_KEY = 'handshake_closed';

interface OpenHandshakeCandidate {
  id: string;
  createdAt: string;
  initiatorId: string | null;
  responderId: string | null;
  isConfirmed: boolean;
  isPasserbyPending?: boolean;
}

/** The two players, in a stable order. A passerby has no pair yet. */
function handshakePairKey(handshake: { initiatorId: string | null; responderId: string | null }): string | null {
  if (!handshake.initiatorId || !handshake.responderId) return null;

  return [handshake.initiatorId, handshake.responderId].sort().join(':');
}

/**
 * The open game between two players is their newest live handshake. An older
 * one at another place stays on the list, and its recommendation is closed.
 * A passerby still waiting is its own game.
 */
export function isOpenHandshake(
  handshake: OpenHandshakeCandidate,
  handshakes: OpenHandshakeCandidate[],
  userId: string | null | undefined,
  now: Date,
): boolean {
  if (!canOpenHandshakeGame(handshake, userId, now)) return false;

  const pair = handshakePairKey(handshake);
  if (!pair) return true;

  const made = new Date(handshake.createdAt).getTime();

  return !handshakes.some((other) => {
    if (other.id === handshake.id || handshakePairKey(other) !== pair) return false;
    if (!canOpenHandshakeGame(other, userId, now)) return false;

    return new Date(other.createdAt).getTime() > made;
  });
}

/** A round with no stored position. The sheet then draws the seed's own rank. */
const UNDEALT_POSITION = -1;

/** Puts a round's dealt position where the sheet reads it, leaving earlier rounds alone. */
export function placeDrawnPosition(
  existing: readonly number[] | null | undefined,
  round: number,
  position: number,
): number[] {
  const drawn = Array.from(
    { length: Math.max(existing?.length ?? 0, round + 1) },
    (_, index) => existing?.[index] ?? UNDEALT_POSITION,
  );
  drawn[round] = position;

  return drawn;
}

export interface HandshakePositionChoiceInput {
  /** A handshake exists that can hold the position. */
  hasHandshake: boolean;
  /** This position has been on the handshake, so editing it writes at once. */
  committed: boolean;
  /** It is on the handshake right now. */
  saved: boolean;
  /** The box for a position PLAY has not saved yet. It starts on. */
  intent: boolean;
}

export interface HandshakePositionChoice {
  checked: boolean;
  /** PLAYING. The position is saved on the handshake, wherever the sheet was opened. */
  playing: boolean;
  /** The box writes now, rather than only remembering the choice. */
  writesImmediately: boolean;
  /** PLAY keeps a question for this position. Played is what writes it. */
  playSaves: boolean;
}

/**
 * PLAY asks whether this position was played, and Yes is what writes it.
 * One already saved is taken off by the box itself. PLAYING is that saved
 * position, on the clock, from the chooser, or from a profile, spot or gambit.
 */
export function handshakePositionChoice(input: HandshakePositionChoiceInput): HandshakePositionChoice {
  if (!input.hasHandshake) {
    return { checked: input.intent, playing: false, writesImmediately: false, playSaves: false };
  }

  if (input.committed) {
    return { checked: input.saved, playing: input.saved, writesImmediately: true, playSaves: !input.saved };
  }

  return { checked: input.intent, playing: false, writesImmediately: false, playSaves: !input.saved };
}

/** What this handshake last showed, so the next opening does not guess. */
export interface RememberedHandshakePositions {
  /** On the handshake right now. */
  saved: number[];
  /** Have been on it at least once, so the box writes immediately. */
  committed: number[];
  /** The positions dealt for each round, when the server has said. */
  suggestionPositions: number[] | null;
  /** Answered not played, so the toolbar opens on its settled buttons. */
  declined: number[];
  /** Play started and nobody has answered, on either phone. */
  open: number[];
}

const handshakePositions = new Map<string, RememberedHandshakePositions>();

/** Null until this handshake's positions have been read or written in this session. */
export function rememberedHandshakePositions(handshakeId: string): RememberedHandshakePositions | null {
  const memory = handshakePositions.get(handshakeId);
  if (!memory) return null;

  return {
    saved: [...memory.saved],
    committed: [...memory.committed],
    suggestionPositions: memory.suggestionPositions ? [...memory.suggestionPositions] : null,
    declined: [...memory.declined],
    open: [...memory.open],
  };
}

/**
 * Keep the server's list, or the list just written. A position once saved stays
 * committed after it is taken off, so reopening does not treat the untick as new.
 */
export function rememberHandshakePositions(
  handshakeId: string,
  saved: number[],
  extra: { committed?: number[]; suggestionPositions?: number[] | null; declined?: number[]; open?: number[] } = {},
): void {
  const previous = handshakePositions.get(handshakeId);
  const committed = [...new Set([...(previous?.committed ?? []), ...saved, ...(extra.committed ?? [])])];
  const suggestionPositions =
    extra.suggestionPositions !== undefined ? extra.suggestionPositions : (previous?.suggestionPositions ?? null);
  const declined = extra.declined ?? previous?.declined ?? [];
  const open = extra.open ?? previous?.open ?? [];

  handshakePositions.set(handshakeId, {
    saved: [...saved],
    committed,
    suggestionPositions: suggestionPositions ? [...suggestionPositions] : null,
    declined: [...declined],
    open: [...open],
  });
}

/** Played again after a No. The kept No must not answer the new question. */
export function forgetDeclinedPosition(handshakeId: string, position: number): void {
  const memory = handshakePositions.get(handshakeId);
  if (!memory) return;

  memory.declined = memory.declined.filter((declined) => declined !== position);
}

/** An untick PLAY has not saved yet, so closing the sheet does not check it again. */
const unsavedPositionIntent = new Map<string, boolean>();

function unsavedPositionKey(owner: string, position: number): string {
  return `${owner}:${position}`;
}

/** A position PLAY has not saved. Absent means the box starts checked. */
export function unsavedPositionChecked(owner: string, position: number): boolean {
  return unsavedPositionIntent.get(unsavedPositionKey(owner, position)) ?? true;
}

/** Remember an untick. Checking the box again forgets it, and so does PLAY saving it. */
export function rememberUnsavedPosition(owner: string, position: number, checked: boolean): void {
  const key = unsavedPositionKey(owner, position);
  if (checked) unsavedPositionIntent.delete(key);
  else unsavedPositionIntent.set(key, false);
}

/**
 * A time and a colour chosen before a coplayer sits. The sheet that opens once
 * they do reads this, and the handshake is brought into line with it.
 */
export interface HandshakeGameDraft {
  clockChosen: boolean;
  minutes: number | null;
  increment: number | null;
  /** Null keeps the seed's colour. */
  initiatorPlaysWhite: boolean | null;
}

const gameDrafts = new Map<string, HandshakeGameDraft>();

export function rememberHandshakeGameDraft(seed: string, draft: HandshakeGameDraft): void {
  gameDrafts.set(seed, { ...draft });
}

export function readHandshakeGameDraft(seed: string): HandshakeGameDraft | null {
  const draft = gameDrafts.get(seed);
  if (!draft) return null;

  return { ...draft };
}

const SEED_PLAYERS: [HandshakeGamePlayer, HandshakeGamePlayer] = [
  { id: 'initiator', username: 'initiator', role: 'initiator' },
  { id: 'responder', username: 'responder', role: 'responder' },
];

/**
 * What still has to be written onto the handshake so it matches the sheet.
 * Nothing, once the handshake already has that time and colour.
 */
export function handshakeGameDraftChange(
  draft: HandshakeGameDraft,
  saved: {
    initiatorPlaysWhite?: boolean | null;
    clockChosen?: boolean;
    minutes?: number | null;
    increment?: number | null;
  },
  seed: string,
  round = 0,
): { swapColours?: true; minutes?: number; increment?: number } | null {
  const seedWhite = createHandshakeGameSuggestion(seed, SEED_PLAYERS, round).white.role === 'initiator';
  const savedWhite = saved.initiatorPlaysWhite ?? seedWhite;
  const wantedWhite = draft.initiatorPlaysWhite ?? seedWhite;
  const swap = savedWhite !== wantedWhite;
  const wantedClock =
    draft.clockChosen &&
    draft.minutes != null &&
    (!saved.clockChosen || saved.minutes !== draft.minutes || (saved.increment ?? 0) !== (draft.increment ?? 0));

  if (!swap && !wantedClock) return null;

  const change: { swapColours?: true; minutes?: number; increment?: number } = {};
  if (swap) change.swapColours = true;
  if (wantedClock && draft.minutes != null) {
    change.minutes = draft.minutes;
    change.increment = draft.increment ?? 0;
  }

  return change;
}

function hashString(value: string): number {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33 + value.charCodeAt(index)) % 4294967296;
  }
  return hash;
}

/**
 * Seed for one round of a handshake.
 *
 * Round zero is the hash of the id alone: that is the game a handshake has
 * always drawn, and counting rounds must not change it.
 *
 * A later round puts its number in front of the id. Hung on the end, ":1" and
 * ":2" hash to neighbours, and the first draw only reads the top bit of one
 * step of the generator. That bit stays put, so the same player kept white
 * for suggestion after suggestion while the time control and the position,
 * drawn afterwards, moved.
 */
function seedForRound(handshakeId: string, round: number): number {
  if (round <= 0) return hashString(handshakeId);

  return hashString(`${round}:${handshakeId}`);
}

function createRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223 + 4294967296) % 4294967296;
    return state / 4294967296;
  };
}

function takeRandom<T>(values: T[], random: () => number): T {
  return values.splice(Math.floor(random() * values.length), 1)[0];
}

const LIGHT_FILES = [1, 3, 5, 7];
const DARK_FILES = [0, 2, 4, 6];
const KNIGHT_PAIRS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [0, 2],
  [0, 3],
  [0, 4],
  [1, 2],
  [1, 3],
  [1, 4],
  [2, 3],
  [2, 4],
  [3, 4],
];

/** Chess960 numbers a rank. The ordinary starting position is 518. */
export function freestyleNumber(rank: readonly string[]): number {
  const lightBishop = LIGHT_FILES.indexOf(rank.findIndex((piece, file) => piece === 'B' && file % 2 === 1));
  const darkBishop = DARK_FILES.indexOf(rank.findIndex((piece, file) => piece === 'B' && file % 2 === 0));
  const bishopCode = darkBishop * 4 + lightBishop;
  const withoutBishops = rank.flatMap((piece, file) => (piece === 'B' ? [] : [file]));
  const queenCode = withoutBishops.indexOf(rank.indexOf('Q'));
  const withoutQueen = withoutBishops.filter((file) => rank[file] !== 'Q');
  const knightSlots = withoutQueen.flatMap((file, slot) => (rank[file] === 'N' ? [slot] : []));
  const knightCode = KNIGHT_PAIRS.findIndex(([left, right]) => left === knightSlots[0] && right === knightSlots[1]);

  return knightCode * 96 + queenCode * 16 + bishopCode;
}

/** The back rank of a Chess960 number, from the a-file. */
export function backRankFromNumber(position: number): string[] {
  const rank = Array<string>(8).fill('');
  const bishopCode = position % 16;
  const queenCode = Math.floor(position / 16) % 6;
  const knightCode = Math.floor(position / 96);

  rank[LIGHT_FILES[bishopCode % 4]] = 'B';
  rank[DARK_FILES[Math.floor(bishopCode / 4)]] = 'B';

  let open = rank.flatMap((piece, file) => (piece ? [] : [file]));
  rank[open[queenCode]] = 'Q';
  open = rank.flatMap((piece, file) => (piece ? [] : [file]));

  const [left, right] = KNIGHT_PAIRS[knightCode];
  rank[open[left]] = 'N';
  rank[open[right]] = 'N';
  open = rank.flatMap((piece, file) => (piece ? [] : [file]));

  rank[open[0]] = 'R';
  rank[open[1]] = 'K';
  rank[open[2]] = 'R';

  return rank;
}

/**
 * Builds one of the 960 legal freestyle back ranks: bishops on opposite
 * colours, then queen and knights, with the king between the remaining rooks.
 */
function createFreestyleBackRank(random: () => number): string[] {
  const rank = Array<string>(8);
  const openFiles = [0, 1, 2, 3, 4, 5, 6, 7];

  const darkBishopFile = takeRandom([0, 2, 4, 6], random);
  const lightBishopFile = takeRandom([1, 3, 5, 7], random);
  rank[darkBishopFile] = 'B';
  rank[lightBishopFile] = 'B';
  openFiles.splice(openFiles.indexOf(darkBishopFile), 1);
  openFiles.splice(openFiles.indexOf(lightBishopFile), 1);

  rank[takeRandom(openFiles, random)] = 'Q';
  rank[takeRandom(openFiles, random)] = 'N';
  rank[takeRandom(openFiles, random)] = 'N';

  openFiles.sort((left, right) => left - right);
  rank[openFiles[0]] = 'R';
  rank[openFiles[1]] = 'K';
  rank[openFiles[2]] = 'R';

  return rank;
}

export function createHandshakeGameSuggestion(
  handshakeId: string,
  players: [HandshakeGamePlayer, HandshakeGamePlayer],
  /**
   * How many times the two have asked for another game off this handshake.
   * Zero is the first recommendation. The same round is the same game on both
   * phones; the next round is a different one.
   */
  round = 0,
  /**
   * A position dealt and stored for this round, when the seed's own rank is
   * one either player already has. Left out, the rank is drawn from the seed.
   */
  positionIndex?: number | null,
  /**
   * A choice the two have stored. Colour replaces the seed's draw. A clock
   * they changed is kept. Otherwise the time is 10+3. The seed still takes
   * the two throws the old roll used, so the rank drawn after them does not move.
   */
  deal?: {
    initiatorPlaysWhite?: boolean | null;
    minutes?: number | null;
    increment?: number | null;
    clockChosen?: boolean;
  },
): HandshakeGameSuggestion {
  const random = createRandom(seedForRound(handshakeId, round));
  // Handshake roles do not change when a passerby later claims the empty side.
  // Ordering by role therefore keeps colours stable while the placeholder
  // becomes a member, and also gives both phones the same order.
  const orderedPlayers = [...players].sort((left, right) => left.role.localeCompare(right.role));
  const whiteIndex = Math.floor(random() * orderedPlayers.length);
  // The roll is paused. These two throws stay so the rank after them does not move.
  random();
  random();
  const initiator = orderedPlayers.find((player) => player.role === 'initiator') ?? orderedPlayers[0];
  const other = orderedPlayers.find((player) => player !== initiator) ?? orderedPlayers[1];
  const chosenWhite = deal?.initiatorPlaysWhite == null ? null : deal.initiatorPlaysWhite ? initiator : other;
  const chosenBlack = chosenWhite == null ? null : chosenWhite === initiator ? other : initiator;

  return {
    white: chosenWhite ?? orderedPlayers[whiteIndex],
    black: chosenBlack ?? orderedPlayers[1 - whiteIndex],
    minutes: deal?.clockChosen && deal.minutes != null ? deal.minutes : DEFAULT_CHESS_CLOCK_CONTROL.minutes,
    increment: deal?.clockChosen && deal.increment != null ? deal.increment : DEFAULT_CHESS_CLOCK_CONTROL.increment,
    backRank:
      positionIndex != null && positionIndex >= 0 && positionIndex < 960
        ? backRankFromNumber(positionIndex)
        : createFreestyleBackRank(random),
  };
}

/**
 * The rank as it stands on one player's side of the board: their own pieces,
 * left to right from their own seat. Black sits across the board, so his rank
 * runs h to a and mirrors white's. The leftmost square stays dark for both
 * colours, the same corner the position marks use.
 *
 * Anyone who is neither player is shown white's side, which is the one a board
 * is set up from.
 */
export function getPlayerBackRank(suggestion: HandshakeGameSuggestion, playerId?: string | null): HandshakeGameRank {
  if (suggestion.black.id !== playerId) return { pieces: suggestion.backRank };

  return {
    pieces: [...suggestion.backRank].reverse().map((piece) => piece.toLowerCase()),
  };
}
